import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ReferralTree.css';
import SoundManager from '../services/SoundManager';
import { formatNumber } from '../utils/formatters';

// Хелпер для виброотклика
const triggerHaptic = (type: 'light' | 'medium' | 'heavy' = 'light') => {
  if ((window as any).Telegram?.WebApp?.HapticFeedback) {
    (window as any).Telegram.WebApp.HapticFeedback.impactOccurred(type);
  }
};

interface ReferralNode {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  total_referrals: number;
  direct_referrals_count: number;
  has_children: boolean;
  level: number;
  has_active_staking: boolean;
  lunar_staking: number;
  star_staking: number;
  marathon_staking: number;
}

interface ReferralProfileData {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
  telegramId: string;
  rank: string;
  vipStatus: string | null;
  leaderStatus: string | null;
  createdAt: string;
  joinedDaysAgo: number;
  lastActive: string | null;
  lastActiveDaysAgo: number | null;
  referralLevel: number;
  directReferrals: number;
  teamSize: number;
  lunarStaking: number;
  starStaking: number;
  marathonStaking: number;
  totalStaking: number;
  activeStakingsCount: number;
  totalStakingsCount: number;
  referralEarnings: number;
}

interface ReferralTreeProps {
  userId?: number;
  parentId?: number;
  level: number;
  isOpen?: boolean;
  maxUnlockedLevel?: number;
}

const ReferralTree: React.FC<ReferralTreeProps> = ({ userId, parentId, level, isOpen = false, maxUnlockedLevel = 5 }) => {
  const { t } = useTranslation();
  const [referrals, setReferrals] = useState<ReferralNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<{ [key: number]: boolean }>({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true); // Всегда открыто
  const [showLockedMessage, setShowLockedMessage] = useState<number | null>(null);
  const [profileModal, setProfileModal] = useState<ReferralProfileData | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Проверяем, разблокирован ли следующий уровень
  const isNextLevelUnlocked = (level + 1) <= maxUnlockedLevel;

  const PAGE_SIZE = 20;

  useEffect(() => {
    loadReferrals(1);
  }, [level]);

  const loadReferrals = async (pageNum: number) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const params = new URLSearchParams({
        level: level.toString(),
        page: pageNum.toString(),
        limit: PAGE_SIZE.toString()
      });

      if (parentId) {
        params.append('parent_id', parentId.toString());
      }

      console.log('🌳 Загрузка рефералов:', { level, parentId, page: pageNum });

      const response = await fetch(`/api/referrals/tree?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('🌳 Ответ от сервера:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('🌳 Данные рефералов:', result);

        if (result.success) {
          if (pageNum === 1) {
            setReferrals(result.data.referrals);
          } else {
            setReferrals(prev => [...prev, ...result.data.referrals]);
          }
          setHasMore(result.data.hasMore);
          setPage(pageNum);
        }
      } else {
        const errorText = await response.text();
        console.error('🌳 Ошибка API:', response.status, errorText);
      }
    } catch (error) {
      console.error('🌳 Ошибка загрузки рефералов:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (refId: number) => {
    SoundManager.playButtonClick();
    triggerHaptic();

    // Если следующий уровень заблокирован, показываем сообщение
    if (!isNextLevelUnlocked) {
      setShowLockedMessage(refId);
      setTimeout(() => setShowLockedMessage(null), 3000);
      return;
    }

    setExpanded(prev => ({
      ...prev,
      [refId]: !prev[refId]
    }));
  };

  const loadMore = () => {
    SoundManager.playButtonClick();
    triggerHaptic();
    loadReferrals(page + 1);
  };

  const loadProfile = async (refId: number) => {
    SoundManager.playButtonClick();
    triggerHaptic('medium');
    setProfileLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/referrals/user/${refId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setProfileModal(result.data);
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  const formatName = (ref: ReferralNode) => {
    if (ref.username) {
      return `@${ref.username}`;
    }
    const parts = [];
    if (ref.first_name) parts.push(ref.first_name);
    if (ref.last_name) parts.push(ref.last_name);
    return parts.length > 0 ? parts.join(' ') : `ID: ${ref.id}`;
  };

  const getLastActiveText = (profile: ReferralProfileData) => {
    if (profile.lastActiveDaysAgo === null) return t('referralProfile.lastActiveNever');
    if (profile.lastActiveDaysAgo === 0) return t('referralProfile.lastActiveToday');
    return t('referralProfile.lastActiveDaysAgo', { days: profile.lastActiveDaysAgo });
  };

  return (
    <div className="referral-tree">
      {loading && page === 1 ? (
        <div className="tree-loading">{t('referralTree.loading')}</div>
      ) : (
        <>
          {referrals.length === 0 && !loading ? (
            <div className="tree-empty">{t('referralTree.noReferrals', { level })}</div>
          ) : (
            <div className="tree-list">
              {referrals.map(ref => (
                <div key={ref.id} className={`tree-node ${ref.has_active_staking ? 'active-referral' : ''}`}>
                  <div className="node-content">
                    <div className="node-info">
                      {ref.has_children && (
                        <button
                          className={`expand-btn ${expanded[ref.id] ? 'expanded' : ''} ${showLockedMessage === ref.id ? 'locked' : ''}`}
                          onClick={() => toggleExpand(ref.id)}
                        >
                          {expanded[ref.id] ? '−' : '+'}
                        </button>
                      )}
                      {!ref.has_children && <div className="expand-spacer" />}

                      <div className="node-details">
                        <span className="node-name" onClick={(e) => { e.stopPropagation(); loadProfile(ref.id); }} style={{ cursor: 'pointer' }}>
                          {ref.has_active_staking && <span className="active-badge">✅</span>}
                          {formatName(ref)}
                        </span>
                        <span className="node-stats">
                          {ref.total_referrals > 0 && (
                            <span className="node-count">
                              👥 {ref.total_referrals}
                            </span>
                          )}
                          {ref.has_active_staking && (
                            <span className="node-staking-types">
                              {parseFloat(String(ref.lunar_staking)) > 0 && (
                                <span className="staking-type lunar"><span className="staking-icon">☾</span> {parseFloat(String(ref.lunar_staking)).toFixed(0)}</span>
                              )}
                              {parseFloat(String(ref.star_staking)) > 0 && (
                                <span className="staking-type star"><span className="staking-icon">★</span> {parseFloat(String(ref.star_staking)).toFixed(0)}</span>
                              )}
                              {parseFloat(String(ref.marathon_staking)) > 0 && (
                                <span className="staking-type marathon"><span className="staking-icon">M</span> {parseFloat(String(ref.marathon_staking)).toFixed(0)}</span>
                              )}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {showLockedMessage === ref.id && (
                    <div className="locked-level-message">
                      🔒 {t('referralTree.levelLocked', { level: level + 1 })}
                    </div>
                  )}

                  {expanded[ref.id] && ref.has_children && (
                    <div className="node-children">
                      {isNextLevelUnlocked ? (
                        <ReferralTree
                          parentId={ref.id}
                          level={level + 1}
                          isOpen={true}
                          maxUnlockedLevel={maxUnlockedLevel}
                        />
                      ) : (
                        <div className="locked-level-placeholder">
                          🔒 {t('referralTree.levelLocked', { level: level + 1 })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {hasMore && (
            <button
              className="load-more-btn"
              onClick={loadMore}
              disabled={loading}
            >
              {loading ? t('common.loading') : t('referralTree.loadMore')}
            </button>
          )}
        </>
      )}

      {/* Profile Modal */}
      {(profileModal || profileLoading) && (
        <div className="referral-profile-overlay" onClick={() => { setProfileModal(null); setProfileLoading(false); }}>
          <div className="referral-profile-modal" onClick={(e) => e.stopPropagation()}>
            <button className="profile-modal-close" onClick={() => { setProfileModal(null); setProfileLoading(false); }}>✕</button>

            {profileLoading && !profileModal ? (
              <div className="profile-loading">{t('referralProfile.loading')}</div>
            ) : profileModal && (
              <>
                <div className="profile-header">
                  <div className="profile-avatar">
                    {(profileModal.firstName || profileModal.username || '?')[0].toUpperCase()}
                  </div>
                  <div className="profile-name-block">
                    <div className="profile-display-name">
                      {profileModal.username ? `@${profileModal.username}` : profileModal.firstName || `ID: ${profileModal.id}`}
                    </div>
                    <div className="profile-meta">
                      {t('referralProfile.level', { level: profileModal.referralLevel })} &middot; {t('referralProfile.joinedAgo', { days: profileModal.joinedDaysAgo })}
                    </div>
                    {profileModal.rank && (
                      <div className="profile-badge">{profileModal.rank}</div>
                    )}
                  </div>
                </div>

                <div className="profile-stats-grid">
                  <div className="profile-stat-item">
                    <span className="profile-stat-value">{profileModal.directReferrals}</span>
                    <span className="profile-stat-label">{t('referralProfile.directReferrals')}</span>
                  </div>
                  <div className="profile-stat-item">
                    <span className="profile-stat-value">{profileModal.teamSize}</span>
                    <span className="profile-stat-label">{t('referralProfile.teamSize')}</span>
                  </div>
                  <div className="profile-stat-item">
                    <span className="profile-stat-value">{formatNumber(profileModal.totalStaking, 0)}</span>
                    <span className="profile-stat-label">{t('referralProfile.totalStaking')}</span>
                  </div>
                  <div className="profile-stat-item">
                    <span className="profile-stat-value">{profileModal.activeStakingsCount}</span>
                    <span className="profile-stat-label">{t('referralProfile.activeStakings')}</span>
                  </div>
                </div>

                {(profileModal.lunarStaking > 0 || profileModal.starStaking > 0 || profileModal.marathonStaking > 0) && (
                  <div className="profile-staking-details">
                    {profileModal.lunarStaking > 0 && (
                      <span className="profile-staking-tag lunar">☾ {formatNumber(profileModal.lunarStaking, 0)} SPERM</span>
                    )}
                    {profileModal.starStaking > 0 && (
                      <span className="profile-staking-tag star">★ {formatNumber(profileModal.starStaking, 0)} SPERM</span>
                    )}
                    {profileModal.marathonStaking > 0 && (
                      <span className="profile-staking-tag marathon">M {formatNumber(profileModal.marathonStaking, 0)} USDT</span>
                    )}
                  </div>
                )}

                <div className="profile-activity">
                  <span className="profile-activity-label">{t('referralProfile.lastActive')}</span>
                  <span className={`profile-activity-value ${profileModal.lastActiveDaysAgo !== null && profileModal.lastActiveDaysAgo <= 1 ? 'active' : ''}`}>
                    {getLastActiveText(profileModal)}
                  </span>
                </div>

                {profileModal.username && (
                  <a
                    href={`https://t.me/${profileModal.username}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="profile-message-btn"
                    onClick={() => triggerHaptic()}
                  >
                    💬 {t('referralProfile.message')}
                  </a>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralTree;
